import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  InputBase,
  Menu,
  MenuItem,
} from '@mui/material';
import { MoreVert, FileDownload, FileUpload, Undo, Redo, TableRows, TableChart, SmartToy, CloudUpload } from '@mui/icons-material';
import Badge from '@mui/material/Badge';
import CircularProgress from '@mui/material/CircularProgress';
import { useUndoRedo } from '../../hooks/useUndoRedo';
import { useEstimateData } from '../../contexts/EstimateDataContext';
import { getSectionForNr, TesSection } from '../../utils/sectionUtils';
import AddPositionDialog, { PositionFormState } from './AddPositionDialog';
import { generatePrzedmiar, importAth } from '../../services/estimateService';

interface TakeoffPosition {
  id: number;
  nr: string;
  basis: string; // Podstawa
  attachment: string; // Załącznik / źródło ceny
  description: string; // Opis
  quantity: number; // Ilość
  unit: string; // Jmn
}

// Generuj placeholdery bazując na logice DetailedEstimateTab
export const generateSampleTakeoffRows = (): TakeoffPosition[] => {
  const bases = ['KNR 2-02 0101', 'KNR 4-01 0112', 'KNR 5-05 0303', 'KNR 7-03 0104'];
  const attachments = ['analiza indywidualna', 'analogia', 'wycena własna', ''];
  const desc = [
    'Roboty ziemne – wykopy ręczne\nGłębokość do 1,5 m',
    'Fundamenty betonowe B20\nZbrojenie AIIID',
    'Strop Teriva I 30/51',
    'Murowanie ścian z bloczków silikatowych 24 cm'
  ];
  const units = ['m2', 'm3', 'm', 'szt'];
  return Array.from({ length: 100 }).map((_, i) => ({
    id: i + 1,
    nr: (i + 1).toString(),
    basis: bases[i % bases.length],
    attachment: attachments[i % attachments.length],
    description: desc[i % desc.length],
    quantity: parseFloat((Math.random() * 900 + 50).toFixed(3)),
    unit: units[i % units.length],
  }));
};

interface PrzedmiarTabProps { estimateId:number; onImported?: () => void; }

const PrzedmiarTab: React.FC<PrzedmiarTabProps> = ({ estimateId, onImported }) => {
  const { state: rows, setState: setRows, undo, redo, canUndo, canRedo } = useUndoRedo<TakeoffPosition[]>([]);
  const { setSectionData, data } = useEstimateData();
  const tesRows = (data?.tesRows || []) as TesSection[];
  const [sectionFilter, setSectionFilter] = useState<string>('all');
  const [groupView, setGroupView] = useState<boolean>(true);
  const [search, setSearch] = useState('');
  const [selectedRowId, setSelectedRowId] = useState<number | null>(null);
  const [selectedCell, setSelectedCell] = useState<{ id: number; field: 'basis' | 'attachment' | 'description' | 'quantity' | 'unit' } | null>(null);
  const [editingCell, setEditingCell] = useState<{ id: number; field: 'basis' | 'attachment' | 'description' | 'quantity' | 'unit'; value: string } | null>(null);

  // Dialog dodawania pozycji
  const [addPositionDialog, setAddPositionDialog] = useState(false);

  // Eksport szablonu CSV
  const handleGenerateTemplate = () => {
    const headers = ['nr', 'Podstawa', 'Załącznik', 'Opis', 'Ilość', 'Jmn'];
    const csvContent = headers.join(',') + '\n';
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'szablon_przedmiar.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Import CSV
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleLoadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      const lines = text.split(/\r?\n/).filter(Boolean);
      if (lines.length < 2) return; // brak danych
      // Wykryj separator – w PL Excel często używa ';' zamiast ','
      const detectedDelimiter = lines[0].includes(';') ? ';' : ',';
      const newRows: TakeoffPosition[] = [];
      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(detectedDelimiter);
        const [nr, basis, attachment, description, quantity, unit] = cols.map(c => (c || '').replace(/^"|"$/g, '')); // usuń otaczające cudzysłowy
        if (!nr) continue;
        newRows.push({
          id: i,
          nr: nr.trim(),
          basis: basis?.trim() || '',
          attachment: attachment?.trim() || '',
          description: (description || '').replace(/\\n/g, '\n').trim(),
          quantity: parseFloat(((quantity||'') as string).replace(',', '.')) || 0,
          unit: unit?.trim() || '',
        });
      }
      setRows(() => newRows);
    };
    reader.readAsText(file, 'utf-8');
  };

  // Obsługa przycisku P – otwarcie dialogu
  const handleAddPosition = () => {
    setAddPositionDialog(true);
  };

  const handleInsertPosition = (data: PositionFormState) => {
    // Na razie tylko logujemy – dodawanie do tabeli uproszczonej zostanie zaimplementowane w kolejnych krokach
    console.log('Wstaw pozycję (przedmiar):', data);
  };

  // Obsługa aktualizacji komórki
  const updateCell = (id: number, field: 'basis' | 'attachment' | 'description' | 'quantity' | 'unit', value: string) => {
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [field]: field === 'quantity' ? parseFloat(value) || 0 : value } : r))
    );
  };

  // Menu kontekstowe
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const handleMenuOpen = (e: React.MouseEvent<HTMLButtonElement>, rowId: number) => {
    setMenuAnchorEl(e.currentTarget);
    setSelectedRowId(rowId);
  };
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };
  const handleDeleteRow = () => {
    if (selectedRowId !== null) {
      setRows((prev) => prev.filter((r) => r.id !== selectedRowId));
    }
    handleMenuClose();
  };

  // Filtr tekstowy + dział
  const filteredRows = useMemo(()=>{
    let tmp = rows.filter(r =>
      r.nr.includes(search) ||
      r.basis.toLowerCase().includes(search.toLowerCase()) ||
      r.attachment.toLowerCase().includes(search.toLowerCase()) ||
      r.description.toLowerCase().includes(search.toLowerCase())
    );
    if(sectionFilter !== 'all'){
      tmp = tmp.filter(r => getSectionForNr(r.nr, tesRows)?.id.toString() === sectionFilter);
    }
    return tmp;
  }, [rows, search, sectionFilter, tesRows]);

  const rowsWithHeaders = useMemo(()=>{
    if(!groupView) return filteredRows.map(r=>({type:'row' as const, data:r}));

    const result: Array<{type:'header'|'row'; section?:TesSection; data?:TakeoffPosition; total?:number}> = [];
    tesRows.forEach(sec=>{
      const rowsInSec = filteredRows.filter(r=> getSectionForNr(r.nr, tesRows)?.id === sec.id);
      if(rowsInSec.length===0) return;
      result.push({type:'header', section: sec, total: 0});
      rowsInSec.forEach(r=> result.push({type:'row', data: r}));
    });
    return result;
  }, [filteredRows, tesRows, groupView]);

  // Keyboard navigation & editing
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (editingCell) {
      if (e.key === 'Enter') {
        updateCell(editingCell.id, editingCell.field, editingCell.value);
        setEditingCell(null);
      }
      if (e.key === 'Escape') {
        setEditingCell(null);
      }
      return;
    }

    if (selectedCell) {
      const currentRowIndex = filteredRows.findIndex((r) => r.id === selectedCell.id);
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (currentRowIndex < filteredRows.length - 1) {
          const nextRow = filteredRows[currentRowIndex + 1];
          setSelectedCell({ id: nextRow.id, field: selectedCell.field });
          scrollIntoViewRow(nextRow.id);
        }
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (currentRowIndex > 0) {
          const prevRow = filteredRows[currentRowIndex - 1];
          setSelectedCell({ id: prevRow.id, field: selectedCell.field });
          scrollIntoViewRow(prevRow.id);
        }
      }
      if (e.key === 'Enter' || e.key === 'F2') {
        const row = rows.find((r) => r.id === selectedCell.id);
        if (row) {
          const val = row[selectedCell.field].toString();
          setEditingCell({ id: row.id, field: selectedCell.field, value: val });
        }
      }
      if (e.key.length === 1) {
        // natychmiast edycja
        const row = rows.find((r) => r.id === selectedCell.id);
        if (row) {
          setEditingCell({ id: row.id, field: selectedCell.field, value: e.key });
        }
      }
      return;
    }

    // Row selection
    const rowsEls = Array.from(document.querySelectorAll('[data-rowid]')) as HTMLElement[]; // header rows pomijamy
    const currentIndex = rowsEls.findIndex((el) => el.dataset.rowid === `row-${selectedRowId}`);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (currentIndex < rowsEls.length - 1) {
        const next = rowsEls[currentIndex + 1];
        const id = parseInt(next.dataset.rowid!.split('-')[1]);
        setSelectedRowId(id);
        scrollIntoViewRow(id);
      }
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (currentIndex > 0) {
        const prev = rowsEls[currentIndex - 1];
        const id = parseInt(prev.dataset.rowid!.split('-')[1]);
        setSelectedRowId(id);
        scrollIntoViewRow(id);
      }
    }
    if (e.key === 'Enter' || e.key === 'F2') {
      if (selectedRowId !== null) {
        setSelectedCell({ id: selectedRowId, field: 'basis' });
      }
    }
    if (e.key.length === 1 && selectedRowId !== null) {
      setSelectedCell({ id: selectedRowId, field: 'basis' });
      setEditingCell({ id: selectedRowId, field: 'basis', value: e.key });
    }

    if ((e.key === 'w' || e.key === 'W') && !editingCell && !addPositionDialog && document.activeElement?.tagName !== 'INPUT') {
      e.preventDefault();
      setGroupView(prev => !prev);
    }
  };

  const scrollIntoViewRow = (id: number) => {
    const rowEl = document.querySelector(`[data-rowid="row-${id}"]`) as HTMLElement | null;
    const container = tableContainerRef.current;
    if (rowEl && container) {
      const rowBottom = rowEl.offsetTop + rowEl.offsetHeight;
      if (rowBottom > container.scrollTop + container.clientHeight) {
        container.scrollTop = rowBottom - container.clientHeight;
      }
      if (rowEl.offsetTop < container.scrollTop) {
        const headerHeight = (container.querySelector('thead') as HTMLElement)?.offsetHeight || 0;
        container.scrollTop = rowEl.offsetTop - headerHeight;
      }
    }
  };

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.metaKey||e.ctrlKey) && !e.shiftKey && e.key.toLowerCase()==='z'){ e.preventDefault(); canUndo && undo(); }
      if (((e.metaKey||e.ctrlKey)&&e.key.toLowerCase()==='y') || ((e.metaKey||e.ctrlKey)&&e.shiftKey&&e.key.toLowerCase()==='z')){ e.preventDefault(); canRedo && redo(); }
    };
    window.addEventListener('keydown', h);
    return ()=>window.removeEventListener('keydown', h);
  }, [canUndo, canRedo, undo, redo]);

  React.useEffect(()=>{ setSectionData('takeoffRows', rows); }, [rows, setSectionData]);

  // AI file upload
  const aiFileInputRef = useRef<HTMLInputElement>(null);
  const [aiFiles, setAiFiles] = useState<File[]>([]);
  const [generating, setGenerating] = useState(false);

  const handleAiFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setAiFiles(files);
  };

  const handleGenerateAi = async () => {
    if(aiFiles.length===0 || generating) return;
    setGenerating(true);
    try{
      // Jeśli wybrano pojedynczy plik .ath – wykonaj deterministyczny import do bazy
      if(aiFiles.length === 1 && /\.ath$/i.test(aiFiles[0].name)){
        const result = await importAth(estimateId, aiFiles[0]);
        console.log('ATH import result:', result);
        alert(`Zaimportowano z ATH: ${result.imported_positions} pozycji, ${result.imported_rms} RMS.`);
        setAiFiles([]);
        // Odśwież nagłówek/pozycje w widoku szczegółów
        try{ onImported && onImported(); }catch{}
      } else {
        // W innych przypadkach – AI parser przedmiaru do tabeli roboczej
        const res = await generatePrzedmiar(estimateId, aiFiles);
        const positions = res.positions as any[];
        const mapped:TakeoffPosition[] = positions.map((p,i)=>({
          id: i+1,
          nr: p.nr || (i+1).toString(),
          basis: p.podstawa || '',
          attachment: p.zalacznik || '',
          description: p.opis || '',
          quantity: parseFloat(p.ilosc)||0,
          unit: p.jmn || ''
        }));
        setRows(()=>mapped);
        setAiFiles([]);
      }
    }catch(err){ console.error(err); alert('Błąd generowania przedmiaru'); }
    finally{ setGenerating(false); }
  };

  return (
    <Box sx={{ height: '100%', outline: 'none' }} tabIndex={0} onKeyDown={handleKeyDown}>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, overflow: 'visible' }}>
        <Tooltip title="Cofnij (⌘Z)"><span><IconButton size="small" onClick={undo} disabled={!canUndo}><Undo fontSize="small"/></IconButton></span></Tooltip>
        <Tooltip title="Ponów (⌘Y)"><span><IconButton size="small" onClick={redo} disabled={!canRedo}><Redo fontSize="small"/></IconButton></span></Tooltip>
        <Tooltip title="Dodaj pozycję"><IconButton size="small" onClick={handleAddPosition}>P</IconButton></Tooltip>
        <Tooltip title="Generuj szablon"><IconButton size="small" onClick={handleGenerateTemplate}><FileDownload fontSize="small" /></IconButton></Tooltip>
        {/* CSV import */}
        <Tooltip title="Wczytaj przedmiar z CSV"><IconButton size="small" onClick={handleLoadClick}><FileUpload fontSize="small" /></IconButton></Tooltip>
        {/* AI file upload */}
        <Tooltip title={aiFiles.length>0 ? `${aiFiles.length} plik/i wybrane` : 'Dodaj pliki (PDF/ATH/PRD)'}>
          <Badge color="success" variant={aiFiles.length>0 ? 'dot' : undefined} overlap="circular">
            <IconButton size="small" onClick={()=>aiFileInputRef.current?.click()} sx={{ color: aiFiles.length>0 ? 'success.main' : 'inherit' }}>
              <CloudUpload fontSize="small" />
            </IconButton>
          </Badge>
        </Tooltip>
        {/* AI generate */}
        <Tooltip title="Generuj przedmiar (AI)">
          <span>
            <IconButton size="small" onClick={handleGenerateAi} disabled={aiFiles.length===0 || generating}>
              {generating ? <CircularProgress size={18} /> : <SmartToy fontSize="small"/>}
            </IconButton>
          </span>
        </Tooltip>

        {/* Przełącznik widoku działów */}
        <Tooltip title={groupView ? 'Widok bez działów' : 'Widok z działami'}>
          <IconButton size="small" onClick={()=>setGroupView(prev=>!prev)}>
            {groupView ? <TableRows fontSize="small"/> : <TableChart fontSize="small"/>}
          </IconButton>
        </Tooltip>

        {/* Kontener prawy */}
        <Box sx={{ display:'flex', alignItems:'center', gap:1, ml:'auto' }}>
          <input type="file" accept=".csv" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileChange} />
          <input type="file" accept=".pdf,.ath,.prd" multiple ref={aiFileInputRef} style={{display:'none'}} onChange={handleAiFilesChange} />
          {/* Filtr działu */}
          <TextField
            select
            size="small"
            value={sectionFilter}
            onChange={e=> setSectionFilter(e.target.value)}
            sx={{
              minWidth: 220,
              overflow: 'visible',
              '& .MuiOutlinedInput-root': {
                overflow: 'visible',
              },
            }}
            onKeyDown={e => {
              e.stopPropagation();
              if (e.key === 'Escape') {
                tableContainerRef.current?.focus();
              }
            }}
          >
            <MenuItem value="all">Wszystkie działy</MenuItem>
            {tesRows.map(sec=> (
              <MenuItem key={sec.id} value={sec.id.toString()}>{sec.nr} – {sec.name}</MenuItem>
            ))}
          </TextField>

          <TextField size="small" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ width: 220 }} onKeyDown={e => {
            e.stopPropagation();
            if (e.key === 'Escape') {
              tableContainerRef.current?.focus();
            }
          }} />
        </Box>
      </Box>

      {/* Tabela */}
      <TableContainer component={Box} ref={tableContainerRef} sx={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto', borderRadius: 0 }}>
        <Table stickyHeader size="small" sx={{
          tableLayout: 'auto',
          fontSize: '0.70rem',
          '& .MuiTableCell-root': { padding: '3px 6px' },
          '& thead .MuiTableCell-root': { fontSize: '0.78rem', fontWeight: 600 }
        }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 70 }}>Nr</TableCell>
              <TableCell sx={{ width: 140 }}>Podstawa</TableCell>
              <TableCell sx={{ width: 160 }}>Załącznik</TableCell>
              <TableCell sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>Opis</TableCell>
              <TableCell align="right" sx={{ width: 110 }}>Ilość</TableCell>
              <TableCell sx={{ width: 70 }}>Jmn</TableCell>
              <TableCell align="center" sx={{ width: 40 }}>A</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rowsWithHeaders.map(item=>{
              if(item.type==='header' && item.section){
                return (
                  <TableRow key={`hdr-${item.section.id}`} sx={{ backgroundColor:'#e0e0e0' }}>
                    <TableCell colSpan={7} sx={{ fontWeight:700 }}>
                      {`${(item.section.nr || '').replace(/\.+$/, '')}. ${item.section.name}`}
                    </TableCell>
                  </TableRow>
                );
              }
              const row = item.data!;
              return (
              <TableRow
                key={row.id}
                tabIndex={0}
                data-rowid={`row-${row.id}`}
                selected={selectedRowId === row.id && !selectedCell}
                onClick={() => {
                  setSelectedRowId(row.id);
                  setSelectedCell(null);
                }}
                sx={selectedRowId === row.id && !selectedCell ? { outline: '2px solid #1a73e8', outlineOffset: -2 } : {}}
              >
                {/* Nr */}
                <TableCell>{row.nr}</TableCell>

                {/* Podstawa */}
                <TableCell
                  sx={selectedCell?.id === row.id && selectedCell.field === 'basis' ? { outline: '2px solid #1a73e8', outlineOffset: -2 } : {}}
                  onClick={() => {
                    setSelectedRowId(row.id);
                    setSelectedCell({ id: row.id, field: 'basis' });
                  }}
                >
                  {editingCell && editingCell.id === row.id && editingCell.field === 'basis' ? (
                    <InputBase
                      value={editingCell.value}
                      onChange={(e) => setEditingCell({ ...editingCell, value: e.target.value })}
                      autoFocus
                      sx={{ p: 0, m: 0, width: '100%', fontSize: 'inherit', lineHeight: 'inherit', height: '100%' }}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === 'Escape') {
                          setEditingCell(null);
                          setSelectedCell(null);
                        }
                        if (e.key === 'Enter') {
                          updateCell(row.id, 'basis', editingCell.value);
                          setEditingCell(null);
                        }
                      }}
                    />
                  ) : (
                    row.basis
                  )}
                </TableCell>

                {/* Załącznik */}
                <TableCell
                  sx={selectedCell?.id === row.id && selectedCell.field === 'attachment' ? { outline: '2px solid #1a73e8', outlineOffset: -2 } : {}}
                  onClick={() => {
                    setSelectedRowId(row.id);
                    setSelectedCell({ id: row.id, field: 'attachment' });
                  }}
                >
                  {editingCell && editingCell.id === row.id && editingCell.field === 'attachment' ? (
                    <InputBase
                      value={editingCell.value}
                      onChange={(e) => setEditingCell({ ...editingCell, value: e.target.value })}
                      autoFocus
                      sx={{ p: 0, m: 0, width: '100%', fontSize: 'inherit', lineHeight: 'inherit', height: '100%' }}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === 'Escape') {
                          setEditingCell(null);
                          setSelectedCell(null);
                        }
                        if (e.key === 'Enter') {
                          updateCell(row.id, 'attachment', editingCell.value);
                          setEditingCell(null);
                        }
                      }}
                    />
                  ) : (
                    row.attachment
                  )}
                </TableCell>

                {/* Opis */}
                <TableCell
                  sx={{ whiteSpace: 'normal', wordBreak: 'break-word', ...(selectedCell?.id === row.id && selectedCell.field === 'description' ? { outline: '2px solid #1a73e8', outlineOffset: -2 } : {}) }}
                  onClick={() => {
                    setSelectedRowId(row.id);
                    setSelectedCell({ id: row.id, field: 'description' });
                  }}
                >
                  {editingCell && editingCell.id === row.id && editingCell.field === 'description' ? (
                    <InputBase
                      multiline
                      value={editingCell.value}
                      onChange={(e) => setEditingCell({ ...editingCell, value: e.target.value })}
                      autoFocus
                      sx={{ p: 0, m: 0, width: '100%', fontSize: 'inherit', lineHeight: 'inherit' }}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          setEditingCell(null);
                          setSelectedCell(null);
                        }
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          updateCell(row.id, 'description', editingCell.value);
                          setEditingCell(null);
                        }
                      }}
                    />
                  ) : (
                    row.description
                  )}
                </TableCell>

                {/* Ilość */}
                <TableCell
                  align="right"
                  sx={selectedCell?.id === row.id && selectedCell.field === 'quantity' ? { outline: '2px solid #1a73e8', outlineOffset: -2 } : {}}
                  onClick={() => {
                    setSelectedRowId(row.id);
                    setSelectedCell({ id: row.id, field: 'quantity' });
                  }}
                >
                  {editingCell && editingCell.id === row.id && editingCell.field === 'quantity' ? (
                    <InputBase
                      value={editingCell.value}
                      onChange={(e) => setEditingCell({ ...editingCell, value: e.target.value })}
                      autoFocus
                      sx={{ p: 0, m: 0, width: '100%', textAlign: 'right', fontSize: 'inherit', lineHeight: 'inherit' }}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === 'Escape') {
                          setEditingCell(null);
                          setSelectedCell(null);
                        }
                        if (e.key === 'Enter') {
                          updateCell(row.id, 'quantity', editingCell.value);
                          setEditingCell(null);
                        }
                      }}
                    />
                  ) : (
                    row.quantity.toFixed(3)
                  )}
                </TableCell>

                {/* Jmn */}
                <TableCell
                  sx={selectedCell?.id === row.id && selectedCell.field === 'unit' ? { outline: '2px solid #1a73e8', outlineOffset: -2 } : {}}
                  onClick={() => {
                    setSelectedRowId(row.id);
                    setSelectedCell({ id: row.id, field: 'unit' });
                  }}
                >
                  {editingCell && editingCell.id === row.id && editingCell.field === 'unit' ? (
                    <InputBase
                      value={editingCell.value}
                      onChange={(e) => setEditingCell({ ...editingCell, value: e.target.value })}
                      autoFocus
                      sx={{ p: 0, m: 0, width: '100%', fontSize: 'inherit', lineHeight: 'inherit', height: '100%' }}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === 'Escape') {
                          setEditingCell(null);
                          setSelectedCell(null);
                        }
                        if (e.key === 'Enter') {
                          updateCell(row.id, 'unit', editingCell.value);
                          setEditingCell(null);
                        }
                      }}
                    />
                  ) : (
                    row.unit
                  )}
                </TableCell>

                {/* Akcje */}
                <TableCell align="center">
                  <IconButton size="small" onClick={(e) => handleMenuOpen(e, row.id)}>
                    <MoreVert fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Menu akcji */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleDeleteRow}>Usuń</MenuItem>
      </Menu>

      {/* Dialog dodawania pozycji */}
      <AddPositionDialog 
        open={addPositionDialog} 
        onClose={() => setAddPositionDialog(false)} 
        onInsert={handleInsertPosition}
        lastPositionNr={rows.length > 0 ? rows[rows.length - 1].nr : undefined}
      />
    </Box>
  );
};

export default PrzedmiarTab; 